import { useNavigate } from 'react-router-dom';
import { useEffect, useLayoutEffect, useState, useRef, useCallback } from 'react';
import { MeshFill, IS_WEBKIT } from '../components/shared/MeshFill';
import { MessageCircle } from 'lucide-react';
import { PaladinLogo } from '../components/shared/PaladinLogo';
import { PixelCrest } from '../components/shared/PixelCrest';
import { signIn } from '../lib/api';
import { InevitradeLogo } from '../components/shared/InevitradeLogo';
import { Meteors } from '../components/shared/Meteors';
import { Starfield } from '../components/shared/Starfield';
import { GLSLHills } from '../components/shared/GLSLHills';
import { Button } from '../components/shared/Button';
import { Badge } from '../components/shared/Badge';
import { useAppState } from '../hooks/useAppState';
import { mockUser } from '../data/mockUser';

export type Phase = 'rest' | 'damage' | 'hold' | 'unwarp' | 'heal' | 'fade';
type Colors6 = [string, string, string, string, string, string];

/* ── hex ↔ rgb helpers ── */
function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function rgbToHex(r: number, g: number, b: number): string {
  return '#' + ((1 << 24) | (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(b)).toString(16).slice(1).toUpperCase();
}
function lerpColor(a: string, b: string, t: number): string {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  return rgbToHex(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t);
}
function lerpColors(a: Colors6, b: Colors6, t: number): Colors6 {
  return a.map((c, i) => lerpColor(c, b[i], t)) as Colors6;
}
/* Lift a palette toward white so small copy stays readable in the darker phases */
function brighten(c: Colors6, t = 0.45): Colors6 {
  return c.map(x => lerpColor(x, '#FFFFFF', t)) as Colors6;
}
function lerpScalar(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/* ── target values per phase ── */
// damage = bright red center hit, hold = cooling embers/brown, unwarp = brown hold,
// heal = green resurrection, fade = green→silver, rest = silver
const PHASE_COLORS: Record<Phase, Colors6> = {
  damage: ['#CC2020', '#FF3333', '#E02828', '#8B2020', '#6B1A1A', '#CC2020'],
  hold:   ['#5A3018', '#8B5528', '#6B3A1A', '#4A2A15', '#2D1A0E', '#6B4020'],  // cooling embers
  unwarp: ['#3A2A1E', '#4D3828', '#3A3025', '#2D2A22', '#1E1E1A', '#3A3025'],  // cooled brown
  heal:   ['#1A4A5C', '#50E6D4', '#2A6B7C', '#1E3A55', '#0E2540', '#1A4A5C'],  // turquoise resurrection
  fade:   ['#5EB0C4', '#7CCFDC', '#8FD8E4', '#4A9AB0', '#2A5E75', '#7CCFDC'],  // teal→cyan bridge
  rest:   ['#8AC5D8', '#5EA0BC', '#B5DEE8', '#4A85A8', '#2A4A5C', '#8CC0D5'],  // icy cyan metallic
};
const PHASE_SPEED: Record<Phase, number>      = { damage: 1.4, hold: 0.8, unwarp: 0.4, heal: 0.3, fade: 0.25, rest: 0.2 };
const PHASE_DISTORTION: Record<Phase, number> = { damage: 1.4, hold: 0.9, unwarp: 0.5, heal: 0.5, fade: 0.45, rest: 0.4 };
const PHASE_SWIRL: Record<Phase, number>      = { damage: 1.0, hold: 0.7, unwarp: 0.4, heal: 0.4, fade: 0.4, rest: 0.4 };

/* Duration (ms) of the JS lerp for each phase transition */
const PHASE_LERP_MS: Record<Phase, number> = {
  damage: 0,      // instant center hit
  hold:   800,    // rapid cool: red → ember/brown
  unwarp: 1200,   // ember settles into dark brown
  heal:   1600,   // resurrection: brown → green
  fade:   1800,   // green → muted teal-silver
  rest:   1600,   // teal-silver → full silver
};

/* ── animated mesh hook: lerps colors + scalars via rAF ── */
interface MeshAnimOpts {
  delayMs?: Partial<Record<Phase, number>>;   // per-phase delay before lerp starts
  lerpMs?: Partial<Record<Phase, number>>;    // per-phase lerp duration overrides
}

export function useAnimatedMeshParams(phase: Phase, opts?: MeshAnimOpts) {
  const [current, setCurrent] = useState({
    colors: PHASE_COLORS.rest,
    speed: PHASE_SPEED.rest,
    distortion: PHASE_DISTORTION.rest,
    swirl: PHASE_SWIRL.rest,
  });

  const fromRef = useRef(current);
  const rafRef = useRef<number>(0);
  const lastPaintRef = useRef(0);
  const delayRef = useRef<ReturnType<typeof setTimeout>>();

  const animate = useCallback((targetPhase: Phase, animOpts?: MeshAnimOpts) => {
    const delay = animOpts?.delayMs?.[targetPhase] ?? 0;
    const duration = animOpts?.lerpMs?.[targetPhase] ?? PHASE_LERP_MS[targetPhase];
    const targetColors = PHASE_COLORS[targetPhase];
    const targetSpeed = PHASE_SPEED[targetPhase];
    const targetDistortion = PHASE_DISTORTION[targetPhase];
    const targetSwirl = PHASE_SWIRL[targetPhase];
    // Damage: instant. Hold: ease-out (heat dissipating fast then slowing).
    // Heal: ease-out (gentle resurrection). Everything else: ease-in-out.
    const useCoolEase = targetPhase === 'hold';
    const useHealEase = targetPhase === 'heal';

    const run = () => {
      // Capture current rendered values as the lerp origin
      const from = { ...fromRef.current };

      if (duration === 0) {
        const next = { colors: targetColors, speed: targetSpeed, distortion: targetDistortion, swirl: targetSwirl };
        fromRef.current = next;
        setCurrent(next);
        return;
      }

      cancelAnimationFrame(rafRef.current);
      const start = performance.now();

      const tick = (now: number) => {
        const elapsed = now - start;
        const t = Math.min(elapsed / duration, 1);
        // Ease curve: cool/heal (ease-out — fast start, gentle end), default (ease-in-out)
        const ease = (useCoolEase || useHealEase) ? 1 - Math.pow(1 - t, 3)
          : t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

        const next = {
          colors: lerpColors(from.colors, targetColors, ease),
          speed: lerpScalar(from.speed, targetSpeed, ease),
          distortion: lerpScalar(from.distortion, targetDistortion, ease),
          swirl: lerpScalar(from.swirl, targetSwirl, ease),
        };
        fromRef.current = next;
        // WebKit's fills are static gradients, so every colour step redraws the whole scene there;
        // ~20 steps a second keeps the fades smooth without stalling the break animation
        if (!IS_WEBKIT || t >= 1 || now - lastPaintRef.current >= 50) {
          lastPaintRef.current = now;
          setCurrent(next);
        }

        if (t < 1) {
          rafRef.current = requestAnimationFrame(tick);
        }
      };

      rafRef.current = requestAnimationFrame(tick);
    };

    clearTimeout(delayRef.current);
    if (delay > 0) {
      delayRef.current = setTimeout(run, delay);
    } else {
      run();
    }
  }, []);

  useEffect(() => {
    animate(phase, opts);
    return () => {
      cancelAnimationFrame(rafRef.current);
      clearTimeout(delayRef.current);
    };
  }, [phase, animate]);

  return current;
}

// Title: damage instant (center burst), cool slightly faster than shield, heal arrives last (bottom→top)
const titleMeshOpts: MeshAnimOpts = {
  delayMs: { heal: 600, fade: 400 },
  lerpMs: { hold: 600, heal: 1400, fade: 1800 },
};

const TITLE_TEXT_STYLE: React.CSSProperties = {
  fontFamily: '"Metamorphous", "Cinzel", serif',
  fontWeight: 400,
  fontSize: '88px',
  letterSpacing: '0.12em',
};

function MeshTitle({ phase }: { phase: Phase }) {
  const isCut = phase === 'damage' || phase === 'hold' || phase === 'unwarp';
  const mesh = useAnimatedMeshParams(phase, titleMeshOpts);

  return (
    <div className="relative mb-8" style={{ width: 840, height: 112, margin: '0 auto 2rem', filter: IS_WEBKIT ? undefined : 'url(#pixelate-title)' }}>
      <svg
        viewBox="0 0 840 112"
        width="840"
        height="112"
        xmlns="http://www.w3.org/2000/svg"
        className="overflow-visible"
      >
        <defs>
          <clipPath id="title-text-clip">
            <text x="420" y="92" textAnchor="middle" style={TITLE_TEXT_STYLE}>PALADIN</text>
          </clipPath>
          <mask id="title-slash-mask" maskUnits="userSpaceOnUse" x="-60" y="-60" width="960" height="232">
            <rect x="-60" y="-60" width="960" height="232" fill="white" />
            <path
              d="M 4,44 L 50,50 L 84,46 L 130,54 L 164,60 L 196,56 L 250,64 L 290,68 L 324,64 L 380,70 L 420,74 L 456,70 L 510,76 L 550,80 L 584,76 L 636,82 L 680,86 L 710,82 L 760,88 L 800,92 L 836,96"
              stroke="black"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                strokeWidth: isCut ? 7 : 0,
                transition: phase === 'damage' ? 'none' : 'stroke-width 2.8s ease-in-out',
              }}
            />
          </mask>
          {/* Arcade banding: bright highlight cap, dark lower band — Kraken-style lettering */}
          <linearGradient id="title-bands" x1="0" y1="26" x2="0" y2="94" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="white" stopOpacity="0.5" />
            <stop offset="0.18" stopColor="white" stopOpacity="0.4" />
            <stop offset="0.32" stopColor="white" stopOpacity="0" />
            <stop offset="0.55" stopColor="#021018" stopOpacity="0" />
            <stop offset="0.72" stopColor="#021018" stopOpacity="0.28" />
            <stop offset="1" stopColor="#021018" stopOpacity="0.5" />
          </linearGradient>
          <filter id="title-glow" x="-20%" y="-60%" width="140%" height="220%">
            <feGaussianBlur stdDeviation="10" />
          </filter>
        </defs>

        {/* WebKit blanks an HTML element given an SVG filter, so there the pixel edges go on the SVG content */}
        <g filter={IS_WEBKIT ? 'url(#pixelate-title)' : undefined}>
        <g mask="url(#title-slash-mask)">
          {/* Back glow — tracks the phase color */}
          <text
            x="420" y="92" textAnchor="middle"
            style={{ ...TITLE_TEXT_STYLE, fill: brighten(mesh.colors)[1], opacity: 0.75 }}
            filter="url(#title-glow)"
          >
            PALADIN
          </text>
          {/* Stacked 3D extrusion */}
          {[7, 6, 5, 4, 3, 2, 1].map(d => (
            <text
              key={d}
              x="420" y={92 + d} textAnchor="middle"
              style={{ ...TITLE_TEXT_STYLE, fill: d > 4 ? '#020A10' : '#062634', stroke: '#020A10', strokeWidth: 8, strokeLinejoin: 'miter', paintOrder: 'stroke' }}
            >
              PALADIN
            </text>
          ))}
          {/* Dark outline */}
          <text
            x="420" y="92" textAnchor="middle"
            style={{ ...TITLE_TEXT_STYLE, fill: 'none', stroke: '#020A10', strokeWidth: 8, strokeLinejoin: 'miter' }}
          >
            PALADIN
          </text>
          {/* Phase-reactive mesh fill */}
          <MeshFill width={840} height={112} clipPath="url(#title-text-clip)"
            colors={brighten(mesh.colors)} speed={mesh.speed} distortion={mesh.distortion} swirl={mesh.swirl} />
          <text x="420" y="92" textAnchor="middle" style={{ ...TITLE_TEXT_STYLE, fill: 'url(#title-bands)' }}>
            PALADIN
          </text>
        </g>
        </g>
      </svg>
    </div>
  );
}

/* Arcade edge: 1px pixel outline + hard 2px drop + soft cyan glow (matches title/shield) */
const ARCADE_EDGE =
  'drop-shadow(1px 0 0 #020A10) drop-shadow(-1px 0 0 #020A10) drop-shadow(0 -1px 0 #020A10) drop-shadow(0 2px 0 #020A10) drop-shadow(0 0 6px rgba(94,200,220,0.35))';
const ARCADE_EDGE_LG =
  'drop-shadow(2px 0 0 #020A10) drop-shadow(-2px 0 0 #020A10) drop-shadow(0 -2px 0 #020A10) drop-shadow(0 4px 0 #020A10) drop-shadow(0 0 10px rgba(94,200,220,0.45))';

/* ── MeshText: rest-phase mesh gradient applied as text fill ── */
// Tight icy-cyan range — reads as solid frosted metal with a slight turquoise wash
const REST_COLORS: Colors6 = ['#9ECFDD', '#7EB8CC', '#B5DEE8', '#6BA5C0', '#88C2D2', '#9ECFDD'];
const UNLOCK_COLORS: Colors6 = ['#3DD9C8', '#50E6D4', '#40D4C0', '#48E0CC', '#55EBD8', '#45D8C4'];
function MeshText({
  children,
  width,
  height,
  fontSize,
  fontWeight = 500,
  letterSpacing = '0.12em',
  className = '',
  colors = REST_COLORS,
  speed = 0.2,
}: {
  children: string;
  width: number;
  height: number;
  fontSize: number;
  fontWeight?: number;
  letterSpacing?: string;
  className?: string;
  colors?: Colors6;
  speed?: number;
}) {
  const clipId = `mesh-text-${fontSize}-${children.replace(/\s/g, '').slice(0, 8)}`;
  // Extra horizontal padding so Silkscreen's first-glyph bearing doesn't clip
  const pad = 20;
  return (
    <div className={className} style={{ width, height, filter: ARCADE_EDGE }}>
      <svg
        viewBox={`${-pad} 0 ${width + pad * 2} ${height}`}
        width={width}
        height={height}
        xmlns="http://www.w3.org/2000/svg"
        style={{ overflow: 'visible' }}
      >
        <defs>
          <clipPath id={clipId}>
            <text
              x={width / 2}
              y={height * 0.78}
              textAnchor="middle"
              style={{
                fontFamily: '"Silkscreen", "VT323", monospace',
                fontWeight: 700,
                fontSize: `${fontSize}px`,
                letterSpacing,
                textTransform: 'uppercase' as const,
              }}
            >
              {children}
            </text>
          </clipPath>
        </defs>
        <MeshFill x={-pad} y={0} width={width + pad * 2} height={height} clipPath={`url(#${clipId})`} colors={colors} speed={speed} />
      </svg>
    </div>
  );
}

export function Landing() {
  // The hero is drawn at a fixed ~900px; on narrower screens the whole block is zoomed down to fit,
  // so phones get exactly the desktop scene (backgrounds stay full-bleed)
  const [heroZoom, setHeroZoom] = useState(1);
  useEffect(() => {
    const fit = () => setHeroZoom(Math.min(1, (window.innerWidth - 48) / 900));
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);
  return <DesktopLanding heroZoom={heroZoom} />;
}

/* iOS WebKit draws nothing through the lock's mask, so there the lock is painted: body and shackle in the
   lock tones, then the rivets, seams and keyhole painted over in the backdrop colour. */
function PaintedLock({ open, colors }: { open: boolean; colors: string[] }) {
  const gid = `lock-tone-${open ? 'open' : 'shut'}`;
  const BG = '#04121A';
  return (
    <g>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
          {colors.map((c, i) => <stop key={i} offset={`${Math.round((i / Math.max(1, colors.length - 1)) * 100)}%`} stopColor={c} />)}
        </linearGradient>
      </defs>
      <path d={open ? 'M15 30 L15 18 C15 8 19 3 26 3 C33 3 37 8 37 18 L37 10' : 'M15 30 L15 18 C15 8 19 3 26 3 C33 3 37 8 37 18 L37 30'}
        stroke={`url(#${gid})`} strokeWidth="5" strokeLinecap="round" fill="none" />
      <path d="M6 28 L6 26 C6 24.5 7 24 8 24 L44 24 C45 24 46 24.5 46 26 L46 28 L46 48 C46 52 40 58 26 60 C12 58 6 52 6 48 Z" fill={`url(#${gid})`} />
      <g fill={BG} stroke={BG}>
        <line x1="26" y1="24" x2="26" y2="60" strokeWidth="1.4" />
        <line x1="6" y1="38" x2="46" y2="38" strokeWidth="1.4" />
        <g stroke="none">
          <circle cx="12" cy="30" r="2" /><circle cx="40" cy="30" r="2" />
          <circle cx="12" cy="48" r="2" /><circle cx="40" cy="48" r="2" />
          <ellipse cx="26" cy="40" rx="3.5" ry="4" />
          <path d="M24 43 L24 50 C24 51 25 51.5 26 51.5 C27 51.5 28 51 28 50 L28 43" />
        </g>
      </g>
    </g>
  );
}

/* Scales the fixed-width hero down to the screen. A transform, not CSS zoom: iOS WebKit misplaces SVG masks
   inside zoomed content, which left the crest and lock fills blank on iPhones. */
function ScaleFit({ scale, children }: { scale: number; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [h, setH] = useState<number>();
  useLayoutEffect(() => {
    if (scale >= 1 || !ref.current) return;
    const el = ref.current;
    const ro = new ResizeObserver(() => setH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [scale]);
  if (scale >= 1) return <>{children}</>;
  return (
    <div style={{ width: 900 * scale, height: h ? h * scale : undefined, margin: '0 auto' }}>
      <div ref={ref} style={{ width: 900, transform: `scale(${scale})`, transformOrigin: 'top left' }}>{children}</div>
    </div>
  );
}

function DesktopLanding({ heroZoom }: { heroZoom: number }) {
  const small = heroZoom < 0.7; // phones: the tagline lines get larger type so they stay readable once scaled
  const navigate = useNavigate();
  // Use real Discord login once the server has it configured; otherwise the dev stand-in
  const [discordLogin, setDiscordLogin] = useState(false);
  useEffect(() => {
    fetch('/api/auth/discord/enabled').then(r => r.json()).then(d => setDiscordLogin(Boolean(d.enabled))).catch(() => undefined);
  }, []);
  const [phase, setPhase] = useState<Phase>('rest');
  const [burstKey, setBurstKey] = useState(0);
  const [healReady, setHealReady] = useState(false);   // gates vortex/glow — true after hill scan converges
  const [starfieldMounted, setStarfieldMounted] = useState(false);   // keeps Starfield DOM alive through fade-out
  const [lockOpen, setLockOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authStep, setAuthStep] = useState<'connect' | 'validating' | 'validated' | 'denied'>('connect');
  const { setUser } = useAppState();
  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  // Shield: damage instant (center burst), cool slightly slower, heal leads (bottom→top resurrection)
  const shieldMeshOpts: MeshAnimOpts = {
    lerpMs: { hold: 1000, unwarp: 1400, heal: 1000 },
  };
  const mesh = useAnimatedMeshParams(phase, shieldMeshOpts);
  // Lock and copy follow the shield a beat behind, so the hit and the heal ripple down the page
  const lockMesh = useAnimatedMeshParams(phase, { delayMs: { damage: 90, hold: 90, heal: 700, fade: 500, rest: 300 } });
  const copyMesh = useAnimatedMeshParams(phase, { delayMs: { damage: 180, hold: 180, heal: 1000, fade: 700, rest: 500 } });

  useEffect(() => {
    // 21s cycle: damage(0) → hold(0.2s) → unwarp(1.5s) → heal(4s) → fade(6.5s) → rest(8.5s) → next hit(21s)
    // center hit → rapid cool to embers → brown hold → green resurrection → silver rest
    const cycle = () => {
      setPhase('damage');
      setBurstKey(k => k + 1);
      setHealReady(false);
      // burstKey used to remount shockwave overlay animation
      setTimeout(() => setPhase('hold'), 200);
      setTimeout(() => setPhase('unwarp' as Phase), 1500);
      // heal phase starts — hills reverse scan begins (800ms)
      setTimeout(() => setPhase('heal'), 4000);
      // Hill scan converges → vortex/glow triggers
      setTimeout(() => setHealReady(true), 4800);
      setTimeout(() => setPhase('fade' as Phase), 7800);
      setTimeout(() => setPhase('rest'), 10000);
    };
    // Bookend with rest: 5s rest before first hit, ~13s rest between cycles
    const INITIAL_REST = 5000;
    const CYCLE_PERIOD = 23000;
    const firstHit = setTimeout(cycle, INITIAL_REST);
    // After first hit, repeat every 21s (animation is ~8.5s, rest fills the remaining ~12.5s)
    const intervalStart = setTimeout(() => {
      const id = setInterval(cycle, CYCLE_PERIOD);
      intervalRef.current = id;
    }, INITIAL_REST);
    return () => {
      clearTimeout(firstHit);
      clearTimeout(intervalStart);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Starfield mount timing matches meteor freeze/unfreeze: mount on phase=heal (when
  // meteors pause), unmount shortly after phase=fade (when meteors resume).
  useEffect(() => {
    if (phase === 'heal' || phase === 'fade') {
      setStarfieldMounted(true);
      return;
    }
    // Short unmount delay — just enough for the faster fade-out transition to finish
    const unmountTimer = setTimeout(() => setStarfieldMounted(false), 1000);
    return () => clearTimeout(unmountTimer);
  }, [phase]);

  return (
    <div className="relative min-h-screen bg-black overflow-hidden">
      <style>{`
        @keyframes glow-breathe {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
          50% { transform: translate(-50%, -50%) scale(1.08); opacity: 0.7; }
        }
        @keyframes damage-burst {
          0% { transform: translate(-50%, -50%) scale(0.2); opacity: 0.9; }
          35% { transform: translate(-50%, -50%) scale(1); opacity: 0.7; }
          100% { transform: translate(-50%, -50%) scale(1.1); opacity: 0; }
        }
        @keyframes auth-modal-in {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes auth-card-in {
          0%   { transform: translateY(20px) scale(0.96); opacity: 0; }
          100% { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes pixel-pulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50%      { opacity: 1;   transform: scale(1.3); }
        }
        @keyframes lock-fall {
          0%   { transform: translateX(0) translateY(0) rotate(0deg); opacity: 1; }
          10%  { transform: translateX(-2px) translateY(6px) rotate(-4deg); opacity: 1; }
          25%  { transform: translateX(-10px) translateY(20px) rotate(-10deg); opacity: 1; }
          45%  { transform: translateX(-25px) translateY(60px) rotate(-18deg); opacity: 0.85; }
          65%  { transform: translateX(-45px) translateY(140px) rotate(-25deg); opacity: 0.55; }
          85%  { transform: translateX(-65px) translateY(260px) rotate(-30deg); opacity: 0.2; }
          100% { transform: translateX(-80px) translateY(400px) rotate(-35deg); opacity: 0; }
        }
        @keyframes terrain-glitch {
          0%   { transform: translateX(0);    filter: brightness(1); }
          8%   { transform: translateX(-6px); filter: brightness(1.3); }
          16%  { transform: translateX(4px);  filter: brightness(1.2); }
          24%  { transform: translateX(-3px); filter: brightness(1.1); }
          35%  { transform: translateX(2px);  filter: brightness(1.05); }
          50%  { transform: translateX(0);    filter: brightness(1); }
          100% { transform: translateX(0);    filter: brightness(1); }
        }
        @keyframes terrain-shake {
          0%, 50%, 100% { transform: translateX(0); }
          8%  { transform: translateX(-6px); }
          16% { transform: translateX(4px); }
          24% { transform: translateX(-3px); }
          35% { transform: translateX(2px); }
        }
        @keyframes red-shockwave {
          0%   { transform: translate(-50%, -50%) scaleX(0.05) scaleY(0.05); opacity: 0; }
          8%   { transform: translate(-50%, -50%) scaleX(0.3)  scaleY(0.08); opacity: 1; }
          45%  { transform: translate(-50%, -50%) scaleX(1.5)  scaleY(0.25); opacity: 0.55; }
          100% { transform: translate(-50%, -50%) scaleX(4)    scaleY(0.4);  opacity: 0; }
        }
        @keyframes heal-vortex-front {
          /* Front pass: visible in front of shield. Sinusoidal x, linear rise. */
          /* x = sin(θ), behind half hidden via separate layer */
          0%      { transform: translateX(calc(var(--vr) *  0.000)) translateY(calc(var(--rise) * 0.000)); opacity: 0;   }
          2%      { transform: translateX(calc(var(--vr) *  0.259)) translateY(calc(var(--rise) * 0.020)); opacity: 0.8; }
          4.2%    { transform: translateX(calc(var(--vr) *  0.500)) translateY(calc(var(--rise) * 0.042)); opacity: 0.85;}
          6.3%    { transform: translateX(calc(var(--vr) *  0.707)) translateY(calc(var(--rise) * 0.063)); opacity: 0.8; }
          8.3%    { transform: translateX(calc(var(--vr) *  0.866)) translateY(calc(var(--rise) * 0.083)); opacity: 0.7; }
          10.4%   { transform: translateX(calc(var(--vr) *  0.966)) translateY(calc(var(--rise) * 0.104)); opacity: 0.5; }
          12.5%   { transform: translateX(calc(var(--vr) *  1.000)) translateY(calc(var(--rise) * 0.125)); opacity: 0;   }
          /* behind — hidden */
          14.6%   { transform: translateX(calc(var(--vr) *  0.966)) translateY(calc(var(--rise) * 0.146)); opacity: 0;   }
          25%     { transform: translateX(calc(var(--vr) *  0.000)) translateY(calc(var(--rise) * 0.250)); opacity: 0;   }
          35.4%   { transform: translateX(calc(var(--vr) * -0.966)) translateY(calc(var(--rise) * 0.354)); opacity: 0;   }
          /* emerge on left side */
          37.5%   { transform: translateX(calc(var(--vr) * -1.000)) translateY(calc(var(--rise) * 0.375)); opacity: 0;   }
          39.6%   { transform: translateX(calc(var(--vr) * -0.966)) translateY(calc(var(--rise) * 0.396)); opacity: 0.5; }
          41.7%   { transform: translateX(calc(var(--vr) * -0.866)) translateY(calc(var(--rise) * 0.417)); opacity: 0.7; }
          43.8%   { transform: translateX(calc(var(--vr) * -0.707)) translateY(calc(var(--rise) * 0.438)); opacity: 0.8; }
          45.8%   { transform: translateX(calc(var(--vr) * -0.500)) translateY(calc(var(--rise) * 0.458)); opacity: 0.85;}
          47.9%   { transform: translateX(calc(var(--vr) * -0.259)) translateY(calc(var(--rise) * 0.479)); opacity: 0.85;}
          50%     { transform: translateX(calc(var(--vr) *  0.000)) translateY(calc(var(--rise) * 0.500)); opacity: 0.85;}
          52.1%   { transform: translateX(calc(var(--vr) *  0.259)) translateY(calc(var(--rise) * 0.521)); opacity: 0.85;}
          54.2%   { transform: translateX(calc(var(--vr) *  0.500)) translateY(calc(var(--rise) * 0.542)); opacity: 0.8; }
          56.3%   { transform: translateX(calc(var(--vr) *  0.707)) translateY(calc(var(--rise) * 0.563)); opacity: 0.7; }
          58.3%   { transform: translateX(calc(var(--vr) *  0.866)) translateY(calc(var(--rise) * 0.583)); opacity: 0.5; }
          60.4%   { transform: translateX(calc(var(--vr) *  0.966)) translateY(calc(var(--rise) * 0.604)); opacity: 0;   }
          /* behind — hidden */
          62.5%   { transform: translateX(calc(var(--vr) *  1.000)) translateY(calc(var(--rise) * 0.625)); opacity: 0;   }
          75%     { transform: translateX(calc(var(--vr) *  0.000)) translateY(calc(var(--rise) * 0.750)); opacity: 0;   }
          85.4%   { transform: translateX(calc(var(--vr) * -0.966)) translateY(calc(var(--rise) * 0.854)); opacity: 0;   }
          /* emerge on left side */
          87.5%   { transform: translateX(calc(var(--vr) * -1.000)) translateY(calc(var(--rise) * 0.875)); opacity: 0;   }
          89.6%   { transform: translateX(calc(var(--vr) * -0.966)) translateY(calc(var(--rise) * 0.896)); opacity: 0.5; }
          91.7%   { transform: translateX(calc(var(--vr) * -0.866)) translateY(calc(var(--rise) * 0.917)); opacity: 0.7; }
          93.8%   { transform: translateX(calc(var(--vr) * -0.707)) translateY(calc(var(--rise) * 0.938)); opacity: 0.35;}
          95.8%   { transform: translateX(calc(var(--vr) * -0.500)) translateY(calc(var(--rise) * 0.958)); opacity: 0.2; }
          97.9%   { transform: translateX(calc(var(--vr) * -0.259)) translateY(calc(var(--rise) * 0.979)); opacity: 0.08;}
          100%    { transform: translateX(calc(var(--vr) *  0.000)) translateY(var(--rise));                opacity: 0;   }
        }
        @keyframes heal-vortex-behind {
          /* Behind pass: only visible through cutouts, behind shield z-order */
          0%      { transform: translateX(calc(var(--vr) *  0.000)) translateY(calc(var(--rise) * 0.000)); opacity: 0;   }
          10.4%   { transform: translateX(calc(var(--vr) *  0.966)) translateY(calc(var(--rise) * 0.104)); opacity: 0;   }
          /* enter behind */
          12.5%   { transform: translateX(calc(var(--vr) *  1.000)) translateY(calc(var(--rise) * 0.125)); opacity: 0;   }
          14.6%   { transform: translateX(calc(var(--vr) *  0.966)) translateY(calc(var(--rise) * 0.146)); opacity: 0.7; }
          16.7%   { transform: translateX(calc(var(--vr) *  0.866)) translateY(calc(var(--rise) * 0.167)); opacity: 0.8; }
          18.8%   { transform: translateX(calc(var(--vr) *  0.707)) translateY(calc(var(--rise) * 0.188)); opacity: 0.85;}
          20.8%   { transform: translateX(calc(var(--vr) *  0.500)) translateY(calc(var(--rise) * 0.208)); opacity: 0.85;}
          22.9%   { transform: translateX(calc(var(--vr) *  0.259)) translateY(calc(var(--rise) * 0.229)); opacity: 0.85;}
          25%     { transform: translateX(calc(var(--vr) *  0.000)) translateY(calc(var(--rise) * 0.250)); opacity: 0.85;}
          27.1%   { transform: translateX(calc(var(--vr) * -0.259)) translateY(calc(var(--rise) * 0.271)); opacity: 0.85;}
          29.2%   { transform: translateX(calc(var(--vr) * -0.500)) translateY(calc(var(--rise) * 0.292)); opacity: 0.85;}
          31.3%   { transform: translateX(calc(var(--vr) * -0.707)) translateY(calc(var(--rise) * 0.313)); opacity: 0.8; }
          33.3%   { transform: translateX(calc(var(--vr) * -0.866)) translateY(calc(var(--rise) * 0.333)); opacity: 0.7; }
          35.4%   { transform: translateX(calc(var(--vr) * -0.966)) translateY(calc(var(--rise) * 0.354)); opacity: 0;   }
          /* exit behind */
          37.5%   { transform: translateX(calc(var(--vr) * -1.000)) translateY(calc(var(--rise) * 0.375)); opacity: 0;   }
          58.3%   { transform: translateX(calc(var(--vr) *  0.866)) translateY(calc(var(--rise) * 0.583)); opacity: 0;   }
          60.4%   { transform: translateX(calc(var(--vr) *  0.966)) translateY(calc(var(--rise) * 0.604)); opacity: 0;   }
          /* enter behind again */
          62.5%   { transform: translateX(calc(var(--vr) *  1.000)) translateY(calc(var(--rise) * 0.625)); opacity: 0;   }
          64.6%   { transform: translateX(calc(var(--vr) *  0.966)) translateY(calc(var(--rise) * 0.646)); opacity: 0.7; }
          66.7%   { transform: translateX(calc(var(--vr) *  0.866)) translateY(calc(var(--rise) * 0.667)); opacity: 0.8; }
          68.8%   { transform: translateX(calc(var(--vr) *  0.707)) translateY(calc(var(--rise) * 0.688)); opacity: 0.85;}
          70.8%   { transform: translateX(calc(var(--vr) *  0.500)) translateY(calc(var(--rise) * 0.708)); opacity: 0.85;}
          72.9%   { transform: translateX(calc(var(--vr) *  0.259)) translateY(calc(var(--rise) * 0.729)); opacity: 0.85;}
          75%     { transform: translateX(calc(var(--vr) *  0.000)) translateY(calc(var(--rise) * 0.750)); opacity: 0.85;}
          77.1%   { transform: translateX(calc(var(--vr) * -0.259)) translateY(calc(var(--rise) * 0.771)); opacity: 0.85;}
          79.2%   { transform: translateX(calc(var(--vr) * -0.500)) translateY(calc(var(--rise) * 0.792)); opacity: 0.8; }
          81.3%   { transform: translateX(calc(var(--vr) * -0.707)) translateY(calc(var(--rise) * 0.813)); opacity: 0.7; }
          83.3%   { transform: translateX(calc(var(--vr) * -0.866)) translateY(calc(var(--rise) * 0.833)); opacity: 0;   }
          85.4%   { transform: translateX(calc(var(--vr) * -0.966)) translateY(calc(var(--rise) * 0.854)); opacity: 0;   }
          100%    { transform: translateX(calc(var(--vr) *  0.000)) translateY(var(--rise));                opacity: 0;   }
        }
      `}</style>
      {/* Horizon haze — cyan glow sitting behind the hill crest */}
      <div
        className="absolute inset-x-0 pointer-events-none z-0"
        style={{
          top: '48%',
          height: '30%',
          background: 'radial-gradient(ellipse 60% 50% at 50% 55%, rgba(61,217,200,0.14) 0%, rgba(74,184,255,0.06) 45%, transparent 75%)',
        }}
      />
      {/* GLSL terrain background — coarse pixelate filter to match shield chunkiness */}
      <div
        className="absolute inset-0 z-0 overflow-hidden"
        style={{
          opacity: (phase === 'damage' || phase === 'hold') ? 0.8 : 0.55,
          transition: phase === 'damage' ? 'none' : 'opacity 1.2s ease-out',
          animation: (phase === 'damage' || phase === 'hold') ? `${IS_WEBKIT ? 'terrain-shake' : 'terrain-glitch'} 0.5s ease-out forwards` : 'none',
          filter: IS_WEBKIT ? undefined : 'url(#pixelate-shield)',
        }}
      >
        <GLSLHills speed={0.3} cameraZ={130} phase={phase} />
      </div>

      {/* Breathing atmospheric glow — turquoise/cyan wash for retro-RPG sky feel */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1200px] h-[1200px]"
          style={{ background: 'radial-gradient(circle, rgba(61,217,200,0.09) 0%, rgba(61,217,200,0.05) 35%, transparent 70%)', animation: 'glow-breathe 12s ease-in-out infinite' }}
        />
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px]"
          style={{ background: 'radial-gradient(ellipse, rgba(74,184,255,0.06) 0%, rgba(74,184,255,0.03) 40%, transparent 70%)', animation: 'glow-breathe 15s ease-in-out 3s infinite' }}
        />
      </div>

      {/* Red center-burst — expands outward from impact point */}
      {(phase === 'damage' || phase === 'hold') && (
        <div
          key={burstKey}
          className="absolute pointer-events-none z-30"
          style={{
            top: '42%',
            left: '50%',
            width: '700px',
            height: '700px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,60,60,0.3) 0%, rgba(220,40,40,0.12) 40%, transparent 70%)',
            animation: 'damage-burst 0.6s ease-out forwards',
          }}
        />
      )}


      {/* Healing screen tint */}
      <div
        className="absolute inset-0 pointer-events-none z-30 transition-opacity"
        style={{
          background: 'radial-gradient(ellipse at center 65%, rgba(80,220,180,0.15) 0%, rgba(40,180,140,0.05) 40%, transparent 70%)',
          opacity: phase === 'heal' ? 0.08 : 0,
          transitionDuration: phase === 'heal' ? '600ms' : '1000ms',
        }}
      />

      {/* Cinematic vignette */}
      <div
        className="absolute inset-0 pointer-events-none z-20"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.4) 100%)',
        }}
      />

      {/* Meteor streaks — originate above the title, rain down toward terrain.
          Wrapped with pixelate filter so meteors + their tails render as chunky pixel art. */}
      <div
        className="absolute top-[5%] left-0 right-0 h-[35%] overflow-visible pointer-events-none z-[5]"
        style={{ filter: IS_WEBKIT ? undefined : 'url(#pixelate-emblem)' }}
      >
        <Meteors number={24} phase={phase} />
      </div>


      {/* Hero */}
      <main className="relative z-10 flex flex-col items-center justify-center min-h-[calc(100vh-64px)] px-6">
        <ScaleFit scale={heroZoom}>
        <div className="text-center max-w-[960px] mx-auto -mt-8">
          {/* Title with mesh gradient fill */}
          <link href="https://fonts.googleapis.com/css2?family=Cinzel:wght@600&display=swap" rel="stylesheet" />
          <MeshTitle phase={phase} />

          {/* Logo */}
          <div className="relative flex items-center justify-center mb-12">
            <div
              className="absolute w-[280px] h-[280px]"
              style={{ background: 'radial-gradient(circle, rgba(74,158,255,0.12) 0%, rgba(74,158,255,0.05) 45%, transparent 75%)', animation: 'glow-breathe 12s ease-in-out 1s infinite' }}
            />
            {/* Healing magic glow — fades in fast when meteors freeze (heal), fades out
                fast when meteors resume (exit heal). */}
            <div
              className="absolute w-[500px] h-[500px] rounded-full pointer-events-none"
              style={{
                background: 'radial-gradient(circle, rgba(80,230,180,0.4) 0%, rgba(60,200,160,0.15) 35%, rgba(40,180,140,0.05) 60%, transparent 80%)',
                opacity: phase === 'heal' ? 0.9 : 0,
                transform: phase === 'heal' ? 'scale(1.15)' : 'scale(0.8)',
                transitionProperty: 'opacity, transform',
                transitionDuration: phase === 'heal' ? '0.5s' : '0.7s',
                transitionTimingFunction: 'ease-out',
              }}
            />

            {/* Healing Starfield aura — mount persists briefly past heal phase so the fast
                fade-out transition can actually animate before the component unmounts. */}
            <div
              className="absolute pointer-events-none"
              style={{
                width: 620,
                height: 620,
                left: '50%',
                top: 'calc(50% - 30px)',
                transform: `translate(-50%, -50%) scale(${phase === 'heal' ? 1 : 0.85})`,
                transitionProperty: 'opacity, transform',
                transitionDuration: phase === 'heal' ? '0.5s' : '0.7s',
                transitionTimingFunction: 'ease-out',
                borderRadius: '50%',
                overflow: 'hidden',
                opacity: phase === 'heal' ? 0.9 : 0,
                maskImage: 'radial-gradient(circle, black 30%, rgba(0,0,0,0.7) 55%, transparent 80%)',
                WebkitMaskImage: 'radial-gradient(circle, black 30%, rgba(0,0,0,0.7) 55%, transparent 80%)',
                zIndex: 5,
              }}
            >
              {starfieldMounted && (
                <Starfield
                  starCount={8000}
                  waveFrequency={18}
                  starEscapeWidth={320}
                  starColor={{ r: 80, g: 230, b: 180 }}
                  maxOpacity={220}
                  rotationSpeed={0.0004}
                  waveSpeed={0.008}
                />
              )}
            </div>
            {/* Healing vortex — BEHIND layer (peeks through cutouts) — waits for beam */}
            {((phase === 'heal' && healReady) || phase === 'fade') && (() => {
              const COUNT = 10;
              const ORBIT_R = 200;
              const RISE = -340;
              const DURATION = 2.8;
              const SPACING = 0.09;
              return (
                <div style={{
                  opacity: phase === 'fade' ? 0 : 1,
                  transition: 'opacity 1.8s ease-out',
                }}>
                  {[...Array(COUNT)].map((_, i) => {
                    const size = 3.5 - (i * 0.15);
                    return (
                      <div
                        key={`vbehind-${i}`}
                        className="absolute rounded-full pointer-events-none"
                        style={{
                          width: Math.max(size, 2),
                          height: Math.max(size, 2),
                          background: i < 3 ? 'rgba(160,255,220,0.95)' : 'rgba(80,230,180,0.85)',
                          left: '50%',
                          bottom: '10%',
                          zIndex: 5,
                          filter: 'blur(0.3px)',
                          boxShadow: `0 0 ${4 - i * 0.3}px rgba(80,230,180,0.6)`,
                          ['--vr' as string]: `${ORBIT_R}px`,
                          ['--rise' as string]: `${RISE}px`,
                          animation: `heal-vortex-behind ${DURATION}s linear ${i * SPACING}s forwards`,
                        }}
                      />
                    );
                  })}
                </div>
              );
            })()}
            {/* SVG filters — finer pixel grid for a crisp retro-RPG feel (Kraken-style),
                not a heavy mosaic. Shield: 3px grid; emblem: 2px grid. */}
            <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden>
              <defs>
                <filter id="pixelate-shield" x="-5%" y="-5%" width="110%" height="110%">
                  <feFlood x="1" y="1" height="1" width="1" />
                  <feComposite width="3" height="3" />
                  <feTile result="dots" />
                  <feComposite in="SourceGraphic" in2="dots" operator="in" />
                  <feMorphology operator="dilate" radius="1.5" />
                </filter>
                <filter id="pixelate-title" x="-10%" y="-60%" width="120%" height="220%">
                  <feFlood x="1" y="1" height="1" width="1" />
                  <feComposite width="3" height="3" />
                  <feTile result="dots" />
                  <feComposite in="SourceGraphic" in2="dots" operator="in" />
                  <feMorphology operator="dilate" radius="1.5" />
                </filter>
                <filter id="pixelate-emblem" x="-5%" y="-5%" width="110%" height="110%">
                  <feFlood x="0" y="0" height="1" width="1" />
                  <feComposite width="2" height="2" />
                  <feTile result="dots" />
                  <feComposite in="SourceGraphic" in2="dots" operator="in" />
                  <feMorphology operator="dilate" radius="1" />
                </filter>
              </defs>
            </svg>

            {/* OUTER div — glitch animation (transform + brightness keyframes) */}
            <div style={{
              position: 'relative',
              zIndex: 10,
              width: small ? 540 : 360,
              height: small ? 540 : 360,
              animation: (phase === 'damage' || phase === 'hold') ? `${IS_WEBKIT ? 'terrain-shake' : 'terrain-glitch'} 0.5s ease-out forwards` : 'none',
            }}>
              <PixelCrest size={small ? 540 : 360} colors={brighten(mesh.colors)} speed={mesh.speed} phase={phase} />
            </div>
            {/* Healing vortex — FRONT layer (visible over shield) — waits for beam */}
            {((phase === 'heal' && healReady) || phase === 'fade') && (() => {
              const COUNT = 10;
              const ORBIT_R = 200;
              const RISE = -340;
              const DURATION = 2.8;
              const SPACING = 0.09;
              return (
                <div style={{
                  opacity: phase === 'fade' ? 0 : 1,
                  transition: 'opacity 1.8s ease-out',
                }}>
                  {[...Array(COUNT)].map((_, i) => {
                    const size = 3.5 - (i * 0.15);
                    return (
                      <div
                        key={`vfront-${i}`}
                        className="absolute rounded-full pointer-events-none"
                        style={{
                          width: Math.max(size, 2),
                          height: Math.max(size, 2),
                          background: i < 3 ? 'rgba(160,255,220,0.95)' : 'rgba(80,230,180,0.85)',
                          left: '50%',
                          bottom: '10%',
                          zIndex: 15,
                          filter: 'blur(0.3px)',
                          boxShadow: `0 0 ${4 - i * 0.3}px rgba(80,230,180,0.6)`,
                          ['--vr' as string]: `${ORBIT_R}px`,
                          ['--rise' as string]: `${RISE}px`,
                          animation: `heal-vortex-front ${DURATION}s linear ${i * SPACING}s forwards`,
                        }}
                      />
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {/* CTA — Lock that unlocks on click */}
          <button
            onClick={() => {
              if (lockOpen) return;
              setLockOpen(true);
              setTimeout(() => {
                setAuthOpen(true);
                setAuthStep('connect');
              }, 1000);
            }}
            aria-label="Enter Paladin"
            className="group relative mb-16 cursor-pointer flex flex-col items-center mx-auto"
            style={{ background: 'none', border: 'none', padding: 0 }}
          >
            <div style={{
              animation: lockOpen ? 'lock-fall 1s ease-in 0.3s forwards' : 'none',
              filter: ARCADE_EDGE_LG,
            }}>
              <svg
                width={small ? 80 : 52}
                height={small ? 98 : 64}
                viewBox="0 0 52 64"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="overflow-visible"
              >
                <defs>
                  {/* Combined mask: lock shape in white, accent cutouts in black */}
                  <mask id="lock-mesh-mask">
                    <rect width="52" height="64" fill="black" />
                    {/* Body shape */}
                    <path d="M6 28 L6 26 C6 24.5 7 24 8 24 L44 24 C45 24 46 24.5 46 26 L46 28 L46 48 C46 52 40 58 26 60 C12 58 6 52 6 48 Z" fill="white" />
                    {/* Shackle */}
                    <path d="M15 30 L15 18 C15 8 19 3 26 3 C33 3 37 8 37 18 L37 30" stroke="white" strokeWidth="5" strokeLinecap="round" fill="none" />
                    {/* Cut out accents */}
                    <line x1="26" y1="24" x2="26" y2="60" stroke="black" strokeWidth="1.4" />
                    <line x1="6" y1="38" x2="46" y2="38" stroke="black" strokeWidth="1.4" />
                    <circle cx="12" cy="30" r="2" fill="black" />
                    <circle cx="40" cy="30" r="2" fill="black" />
                    <circle cx="12" cy="48" r="2" fill="black" />
                    <circle cx="40" cy="48" r="2" fill="black" />
                    <circle cx="15" cy="28" r="2.5" fill="black" />
                    <circle cx="37" cy="28" r="2.5" fill="black" />
                    <ellipse cx="26" cy="40" rx="3.5" ry="4" fill="black" />
                    <path d="M24 43 L24 50 C24 51 25 51.5 26 51.5 C27 51.5 28 51 28 50 L28 43" fill="black" />
                  </mask>
                </defs>

                {/* Unlock state: green shackle lifts, mask hides closed position */}
                {lockOpen && (
                  <mask id="lock-mesh-mask-open">
                    <rect width="52" height="64" fill="black" />
                    <path d="M6 28 L6 26 C6 24.5 7 24 8 24 L44 24 C45 24 46 24.5 46 26 L46 28 L46 48 C46 52 40 58 26 60 C12 58 6 52 6 48 Z" fill="white" />
                    <path d="M15 30 L15 18 C15 8 19 3 26 3 C33 3 37 8 37 18 L37 10" stroke="white" strokeWidth="5" strokeLinecap="round" fill="none" />
                    <line x1="26" y1="24" x2="26" y2="60" stroke="black" strokeWidth="1.4" />
                    <line x1="6" y1="38" x2="46" y2="38" stroke="black" strokeWidth="1.4" />
                    <circle cx="12" cy="30" r="2" fill="black" />
                    <circle cx="40" cy="30" r="2" fill="black" />
                    <circle cx="12" cy="48" r="2" fill="black" />
                    <circle cx="40" cy="48" r="2" fill="black" />
                    <circle cx="15" cy="28" r="2.5" fill="black" />
                    <circle cx="37" cy="12" r="2.5" fill="black" />
                    <ellipse cx="26" cy="40" rx="3.5" ry="4" fill="black" />
                    <path d="M24 43 L24 50 C24 51 25 51.5 26 51.5 C27 51.5 28 51 28 50 L28 43" fill="black" />
                  </mask>
                )}

                {/* Mesh gradient fill — always rendered, mask changes on unlock */}
                {IS_WEBKIT
                  ? <PaintedLock open={lockOpen} colors={lockOpen ? UNLOCK_COLORS : brighten(lockMesh.colors, 0.25)} />
                  : <MeshFill x={0} y={0} width={52} height={64}
                      mask={lockOpen ? 'url(#lock-mesh-mask-open)' : 'url(#lock-mesh-mask)'}
                      colors={lockOpen ? UNLOCK_COLORS : brighten(lockMesh.colors, 0.25)} speed={lockOpen ? 0.4 : 0.2} />}
              </svg>
            </div>
            {/* Hover glow */}
            <div
              className="absolute -inset-4 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{ background: 'radial-gradient(circle, rgba(155,175,196,0.08) 0%, transparent 70%)' }}
            />
            {!lockOpen && (
              <span
                className="block mt-2 tracking-[0.14em] uppercase text-center"
                style={{ fontSize: small ? 24 : 14, fontFamily: '"Silkscreen", "VT323", monospace', filter: ARCADE_EDGE, color: brighten(lockMesh.colors)[1] }}
              >
                Unlock
              </span>
            )}
          </button>

          {/* Single value prop line */}
          <div className="mb-8 flex justify-center">
            <MeshText width={900} height={small ? 38 : 30} fontSize={small ? 22 : 15} fontWeight={700} letterSpacing="0.10em" colors={brighten(copyMesh.colors)}>
              Where traders come to track, train, and level up.
            </MeshText>
          </div>

          {/* Inevitrade qualifier — "Exclusive to" sized to match the Inevitrade logo */}
          <div className="flex items-center justify-center gap-1">
            <MeshText width={small ? 270 : 200} height={small ? 38 : 28} fontSize={small ? 22 : 16} fontWeight={700} letterSpacing="0.10em" className="flex-shrink-0" colors={brighten(copyMesh.colors)}>
              Exclusive to
            </MeshText>
            <InevitradeLogo height={small ? 38 : 28} className="text-white" />
          </div>
        </div>
        </ScaleFit>
      </main>

      {/* Footer */}
      <footer className="relative z-10 py-6 text-center px-6">
        <p
          className={`${small ? 'text-[8px]' : 'text-[11px]'} text-[#5EA0BC]/60 max-w-[500px] mx-auto leading-relaxed tracking-[0.14em] uppercase`}
          style={{ fontFamily: '"Silkscreen", "VT323", monospace', fontWeight: 700, filter: 'drop-shadow(0 1px 0 #020A10)' }}
        >
          Paladin is an educational tool, not investment advice.
        </p>
      </footer>

      {/* Auth modal overlay */}
      {authOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center"
          style={{ animation: 'auth-modal-in 0.4s ease-out forwards' }}
        >
          {/* Pixel-noise backdrop */}
          <div
            className="absolute inset-0"
            onClick={() => { setAuthOpen(false); setLockOpen(false); }}
            style={{
              background: 'rgba(0,0,0,0.75)',
              backdropFilter: 'blur(2px)',
            }}
          />
          {/* Pixel-art dialog box — stepped box-shadow borders, no rounded corners,
              Silkscreen typography, silver/steel palette matching the Landing. */}
          <div
            className="relative w-full max-w-[440px] mx-6 text-center"
            style={{
              background: '#07202A',
              // Pixel-art border: inner highlight + inner shadow + outer dark border, all stepped
              boxShadow: [
                // outer hard pixel border (4px dark)
                '0 0 0 2px #0E3A48',
                '0 0 0 4px #020A10',
                '0 0 0 6px #0E3A48',
                // inner highlight (light-edge) and shadow (bottom-right)
                'inset 2px 2px 0 rgba(138, 197, 216, 0.35)',
                'inset -2px -2px 0 rgba(0, 0, 0, 0.5)',
                // ambient glow
                '0 0 60px rgba(138, 197, 216, 0.08)',
              ].join(', '),
              padding: '32px 28px 28px',
              animation: 'auth-card-in 0.4s ease-out forwards',
              fontFamily: '"Silkscreen", "VT323", monospace',
            }}
          >
            {/* Pixel corner flourishes */}
            <div style={{ position: 'absolute', top: -4, left: -4, width: 10, height: 10, background: '#8AC5D8' }} />
            <div style={{ position: 'absolute', top: -4, right: -4, width: 10, height: 10, background: '#8AC5D8' }} />
            <div style={{ position: 'absolute', bottom: -4, left: -4, width: 10, height: 10, background: '#8AC5D8' }} />
            <div style={{ position: 'absolute', bottom: -4, right: -4, width: 10, height: 10, background: '#8AC5D8' }} />

            <div className="flex justify-center mb-4">
              <PaladinLogo size={56} />
            </div>

            {authStep === 'connect' && (
              <>
                <h2 style={{
                  fontFamily: '"Metamorphous", serif',
                  fontSize: '22px',
                  color: '#D4F1F7',
                  letterSpacing: '0.1em',
                  marginBottom: '8px',
                  textShadow: '0 2px 0 #020A10, 0 0 8px rgba(138, 197, 216, 0.4)',
                }}>
                  DISCORD VERIFICATION
                </h2>
                <p style={{
                  fontFamily: '"Silkscreen", monospace',
                  fontWeight: 700,
                  fontSize: '11px',
                  color: 'rgba(255,255,255,0.55)',
                  letterSpacing: '0.08em',
                  marginBottom: '24px',
                  lineHeight: 1.6,
                }}>
                  CONNECT YOUR DISCORD TO VERIFY YOUR INEVITRADE MEMBERSHIP.
                </p>
                <button
                  onClick={() => {
                    setAuthStep('validating');
                    // Real Discord login: straight to Discord; the welcome with their actual name and role shows on return
                    if (discordLogin) { window.location.href = '/api/auth/discord/login'; return; }
                    setTimeout(() => setAuthStep('validated'), 1200);
                  }}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    background: '#5865F2',
                    color: '#FFFFFF',
                    fontFamily: '"Silkscreen", monospace',
                    fontWeight: 700,
                    fontSize: '12px',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: [
                      '0 0 0 2px #3B44A8',
                      '0 0 0 4px #020A10',
                      'inset 2px 2px 0 rgba(255,255,255,0.2)',
                      'inset -2px -2px 0 rgba(0,0,0,0.25)',
                    ].join(', '),
                  }}
                  className="flex items-center justify-center gap-3"
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#6872F5'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#5865F2'; }}
                >
                  <img
                    src="/assets/crest/scroll-icon.png"
                    alt=""
                    width={22}
                    height={22}
                    style={{ imageRendering: 'pixelated' }}
                  />
                  CONNECT DISCORD
                </button>
                <PassKeySignIn />
              </>
            )}

            {authStep === 'validating' && (
              <>
                <h2 style={{
                  fontFamily: '"Metamorphous", serif',
                  fontSize: '22px',
                  color: '#D4F1F7',
                  letterSpacing: '0.1em',
                  marginBottom: '8px',
                  textShadow: '0 2px 0 #020A10, 0 0 8px rgba(138, 197, 216, 0.4)',
                }}>
                  VERIFYING...
                </h2>
                <p style={{
                  fontFamily: '"Silkscreen", monospace',
                  fontWeight: 700,
                  fontSize: '11px',
                  color: 'rgba(255,255,255,0.5)',
                  letterSpacing: '0.08em',
                  marginBottom: '18px',
                }}>
                  CHECKING YOUR DISCORD ROLE...
                </p>
                {/* Pixel-art loading spinner: 4 square dots rotating */}
                <div className="flex justify-center items-center" style={{ height: 28 }}>
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      style={{
                        width: 8, height: 8, margin: '0 3px', background: '#8AC5D8',
                        animation: `pixel-pulse 1.2s ease-in-out ${i * 0.15}s infinite`,
                      }}
                    />
                  ))}
                </div>
              </>
            )}

            {authStep === 'validated' && (
              <>
                <h2 style={{
                  fontFamily: '"Metamorphous", serif',
                  fontSize: '22px',
                  color: '#50E6D4',
                  letterSpacing: '0.1em',
                  marginBottom: '8px',
                  textShadow: '0 2px 0 #020A10, 0 0 8px rgba(80,230,212,0.5)',
                }}>
                  ✓ VERIFIED
                </h2>
                <p style={{
                  fontFamily: '"Silkscreen", monospace',
                  fontWeight: 700,
                  fontSize: '11px',
                  color: 'rgba(255,255,255,0.55)',
                  letterSpacing: '0.08em',
                  marginBottom: '16px',
                }}>
                  WELCOME, <span style={{ color: '#D4F1F7' }}>{mockUser.discordUsername.toUpperCase()}</span>
                </p>
                <div className="flex justify-center mb-6">
                  <div style={{
                    padding: '6px 14px',
                    background: mockUser.role === 'Inner Circle' ? '#D4A843' : '#4A9EFF',
                    color: '#020A10',
                    fontFamily: '"Silkscreen", monospace',
                    fontWeight: 700,
                    fontSize: '10px',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    boxShadow: [
                      '0 0 0 2px #020A10',
                      'inset 1px 1px 0 rgba(255,255,255,0.35)',
                      'inset -1px -1px 0 rgba(0,0,0,0.25)',
                    ].join(', '),
                  }}>
                    {mockUser.role}
                  </div>
                </div>
                <button
                  onClick={() => {
                    // Real Discord login when the server is configured for it
                    if (discordLogin) { window.location.href = '/api/auth/discord/login'; return; }
                    setUser({ ...mockUser, traderStage: null, currentStrategy: null, currentFirmId: null, currentTier: null });
                    // Open a real API session for this Discord user (dev stand-in until OAuth lands)
                    signIn(mockUser.discordUsername, mockUser.role).catch(err => console.warn('API sign-in failed', err));
                    navigate('/hub');
                  }}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    background: '#0E3A48',
                    color: '#D4F1F7',
                    fontFamily: '"Silkscreen", monospace',
                    fontWeight: 700,
                    fontSize: '12px',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: [
                      '0 0 0 2px #8AC5D8',
                      '0 0 0 4px #020A10',
                      'inset 2px 2px 0 rgba(138,197,216,0.25)',
                      'inset -2px -2px 0 rgba(0,0,0,0.35)',
                    ].join(', '),
                  }}
                >
                  CONTINUE ▶
                </button>
              </>
            )}

            {authStep === 'denied' && (
              <>
                <h2 style={{
                  fontFamily: '"Metamorphous", serif',
                  fontSize: '22px',
                  color: '#b23838',
                  letterSpacing: '0.1em',
                  marginBottom: '8px',
                  textShadow: '0 2px 0 #020A10, 0 0 8px rgba(178,56,56,0.5)',
                }}>
                  ✕ ACCESS DENIED
                </h2>
                <p style={{
                  fontFamily: '"Silkscreen", monospace',
                  fontWeight: 700,
                  fontSize: '11px',
                  color: 'rgba(255,255,255,0.55)',
                  letterSpacing: '0.08em',
                  marginBottom: '24px',
                  lineHeight: 1.6,
                }}>
                  YOU MUST BE AN ACCELERATOR OR INNER CIRCLE MEMBER.
                  JOIN THE INEVITRADE DISCORD TO GET STARTED.
                </p>
                <button
                  onClick={() => setAuthStep('connect')}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    background: '#0E3A48',
                    color: '#D4F1F7',
                    fontFamily: '"Silkscreen", monospace',
                    fontWeight: 700,
                    fontSize: '12px',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: [
                      '0 0 0 2px #8AC5D8',
                      '0 0 0 4px #020A10',
                      'inset 2px 2px 0 rgba(138,197,216,0.25)',
                      'inset -2px -2px 0 rgba(0,0,0,0.35)',
                    ].join(', '),
                  }}
                >
                  TRY AGAIN
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* Invited guests without Discord sign in with a personal pass key; the session lands on the same
   callback page Discord does, so the welcome and Home Base work the same way */
function PassKeySignIn() {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const font = { fontFamily: '"Silkscreen", monospace', fontSize: '10px', letterSpacing: '0.08em' } as const;
  if (!open) return (
    <button type="button" onClick={() => setOpen(true)} style={{ ...font, marginTop: 14, background: 'none', border: 'none', color: 'rgba(212,241,247,0.6)', cursor: 'pointer', textDecoration: 'underline' }}>
      HAVE A PASS KEY?
    </button>
  );
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr(null); setBusy(true);
    try {
      const r = await fetch('/api/auth/passkey', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ key }) });
      const j = await r.json();
      if (!r.ok) { setErr(j.error ?? 'Sign-in failed'); return; }
      window.location.href = `/auth/callback#token=${j.session_token}&name=${encodeURIComponent(j.name)}&roles=${(j.roles as string[]).join(',')}`;
    } catch { setErr('Sign-in failed'); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <input autoFocus value={key} onChange={e => setKey(e.target.value)} placeholder="PALADIN-XXXXX-XXXXX-XXXXX" autoComplete="off" spellCheck={false}
        style={{ ...font, fontSize: '12px', padding: '10px 12px', background: '#020A10', color: '#D4F1F7', border: 'none', boxShadow: '0 0 0 2px #2A5F6F', textAlign: 'center' }} />
      <button type="submit" disabled={busy || key.trim().length < 12}
        style={{ ...font, fontSize: '11px', padding: '10px', background: '#2A5F6F', color: '#D4F1F7', border: 'none', cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
        {busy ? 'CHECKING…' : 'ENTER WITH PASS KEY'}
      </button>
      {err && <div style={{ ...font, color: '#FF8A8A' }}>{err.toUpperCase()}</div>}
    </form>
  );
}
